import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { agentMarkdownSanitizeSchema, hardenAgentUrl } from '@/_shared/lib/agent-markdown';
import { safeAgentMarkdownHref } from '@/_shared/lib/safe-url';

/**
 * An option's description, rendered as hardened, compact markdown: inline emphasis (bold / italic /
 * `code` / links) plus paragraph breaks, so a description can span a few short lines. Scoped to the
 * description only — option labels and question prompts stay plain text. URLs go through the same
 * sanitizer/hardening as the rest of the transcript.
 */
export function OptionDescription({ content }: { content: string }): React.ReactElement {
  return (
    <span className="mt-0.5 block space-y-1 text-xs text-muted-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, agentMarkdownSanitizeSchema]]}
        urlTransform={hardenAgentUrl}
        components={{
          // Each paragraph / heading is its own line (a <span>, since a <p> is invalid inside the
          // wrapping <span>); lists collapse to inline so a stray one can't blow out the option row.
          p: ({ children }) => <span className="block">{children}</span>,
          h1: ({ children }) => <span className="block font-medium">{children}</span>,
          h2: ({ children }) => <span className="block font-medium">{children}</span>,
          h3: ({ children }) => <span className="block font-medium">{children}</span>,
          ul: ({ children }) => <>{children}</>,
          ol: ({ children }) => <>{children}</>,
          li: ({ children }) => <>{children}</>,
          code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 font-mono">{children}</code>,
          a: ({ children, href }) => {
            const safe = safeAgentMarkdownHref(href);
            return safe ? (
              <a href={safe} target="_blank" rel="noopener noreferrer" className="text-brand underline">
                {children}
              </a>
            ) : (
              <>{children}</>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </span>
  );
}
